import { useTranslation } from 'react-i18next';
import { Box, Divider } from '@mui/material';
import { MemberDataGrid, getMemberSearchFilter } from 'src/modules/member/components/memberDataGrid';
import { useState, useCallback, useEffect, useRef } from 'react';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { FormProvider, useForm } from 'react-hook-form';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { MemberStatusTabKeysEnum } from 'src/shared/models/enums/memberStatusTabKeys.enum';
import { getMembers } from 'src/shared/services/memberService';
import { toast } from 'react-toastify';
import { MemberAdvancedSearch } from '../components/memberAdvancedSearch';
import getDefaultMemberSearchFormState, { MemberSearchFormState } from '../models/memberSearchFormState';
import { memberTabs } from '../models/memberTabs';

export interface MemberExportFilter {
  exportProperties: Array<string>;
  sortOrder: string;
  sortField: string;
}

type MemberAdvancedSearchPageProps = {
  otherContactMode: boolean;
};

export const MemberAdvancedSearchPage = (props: MemberAdvancedSearchPageProps) => {
  const { otherContactMode } = props;
  const isMounted = useIsMounted();

  const { t } = useTranslation(['member']);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [userActivatedSearch, setUserActivatedSearch] = useState<boolean>(false);
  const [searchFilterInitialized, setSearchFilterInitialized] = useState<boolean>(false);
  const [searchFilter, setSearchFilter] = useState<MemberSearchFormState>();

  const initialState = getDefaultMemberSearchFormState() as MemberSearchFormState;
  const [advancedSearchFilter, setAdvancedSearchFilter] = useLocalStorage<MemberSearchFormState>(LocalStorageKeys.MemberAdvancedSearchFilter, {
    ...initialState,
    associationIds: [],
    districtIds: [],
    searchString: '',
    postCode: '',
    countyCode: [],
    municipalityCode: [],
    profileItemIds: [],
    excludedProfileItemIds: [],
    admDateStart: '',
    admDateEnd: '',
    resDateStart: '',
    resDateEnd: '',
    dueDateStart: '',
    dueDateEnd: '',
    payDateStart: '',
    payDateEnd: '',
    birthDateStart: '',
    birthDateEnd: '',
    hasMobilePhone: '',
    hasEmailAddress: '',
    hasNewspaper: '',
    isMainMember: '',
    isFamilyMember: '',
    resCause: undefined,
    sex: undefined,
    titleIds: [],
    includeResignedTitles: '',
  } as unknown as MemberSearchFormState);

  const defaultFormValues: MemberSearchFormState = {
    ...initialState,
    associationIds: '',
    districtIds: '',
    searchString: '',
    postCode: '',
    municipalityCode: [],
    status: memberTabs.filter((x) => x.key === MemberStatusTabKeysEnum.Paid || x.key === MemberStatusTabKeysEnum.NotPaid),
    countyCode: [],
    profileItemIds: [],
    excludedProfileItemIds: [],
    admDateStart: '',
    admDateEnd: '',
    resDateStart: '',
    resDateEnd: '',
    dueDateStart: '',
    dueDateEnd: '',
    payDateStart: '',
    payDateEnd: '',
    birthDateStart: '',
    birthDateEnd: '',
    hasMobilePhone: '',
    hasEmailAddress: '',
    hasNewspaper: '',
    isMainMember: '',
    isFamilyMember: '',
    resCause: undefined,
    sex: undefined,
    titleIds: [],
    includeResignedTitles: '',
    selectionId: undefined,
    selectionName: '',
    selectionInfo: '',
  };

  const formMethods = useForm<MemberSearchFormState>({
    defaultValues: defaultFormValues,
    mode: 'all',
  });

  const { handleSubmit, getValues, setValue, reset, watch } = formMethods;
  ConsoleHelper.log('watch', watch());
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!searchFilterInitialized) {
      const filter: MemberSearchFormState = {
        ...initialState,
      };
      let countSearchParams = 0;
      Object.keys(advancedSearchFilter).forEach((key) => {
        const value = advancedSearchFilter[key];
        // Check if value is valid (non-empty array, non-empty string, or defined)
        const isValid = Array.isArray(value)
          ? value.length > 0 // Valid if array and not empty
          : value !== undefined && value !== null && value !== ''; // Valid if not undefined, null, or empty string

        if (isValid) {
          filter[key] = value;
          countSearchParams += 1;
        }
      });
      if (countSearchParams > 1) {
        setUserActivatedSearch(true);
      }
      reset(filter);
      setSearchFilter(filter);
      setSearchFilterInitialized(true); // Mark initialization as complete to avoid re-running this effect
    }
  }, [advancedSearchFilter, initialState, reset, searchFilterInitialized]);

  const onSubmit = (data, e) => {
    e.preventDefault();
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    setUserActivatedSearch(true);
    setAdvancedSearchFilter(data);
    setSearchFilter(data);
  };

  const handleResetAll = () => {
    const excludedKeys = ['sortCol', 'sortDir', 'pageIndex', 'pageSize'];
    const fields = Object.keys(getValues()) as Array<keyof MemberSearchFormState>;
    fields
      .filter((field) => !excludedKeys.includes(field))
      .forEach((field) => {
        if (field === 'status') {
          setValue(field, defaultFormValues.status);
        } else {
          setValue(field, '');
        }
      });
    setUserActivatedSearch(false);
    setAdvancedSearchFilter(defaultFormValues);
    setSearchFilter(defaultFormValues);
  };

  const updateState = (state: MemberSearchFormState) => {
    // Iterate over all properties in defaultFormValues and set state if provided
    (Object.keys(defaultFormValues) as Array<keyof MemberSearchFormState>).forEach((key) => {
      ConsoleHelper.log('resetting:', key, state[key] ?? '');
      setValue(key, state[key] ?? '');
    });
    setUserActivatedSearch(false);
    setAdvancedSearchFilter(state);
    setSearchFilter(state);
  };

  const handleExport = useCallback(
    (format: string, filter: MemberExportFilter) => {
      const fetchData = async () => {
        try {
          const memberSearchFilter = getMemberSearchFilter(getValues(), otherContactMode || false);
          await getMembers(0, 10000, filter.sortField || 'lastName', filter.sortOrder || 'asc', memberSearchFilter, filter.exportProperties, format);
          if (isMounted()) toast.success(<>{t('member:exportInitialized')}</>);
        } catch (e) {
          if (isMounted()) ConsoleHelper.log(e);
        }
      };
      fetchData();
    },
    [getValues, isMounted, otherContactMode, t],
  );

  const handleSearchFilterCallback = useCallback(
    (newFilter) => {
      setTimeout(() => {
        setUserActivatedSearch(true);
        setAdvancedSearchFilter((prev) => ({
          ...prev,
          ...newFilter,
        }));
        setSearchFilter((prev) => ({
          ...prev,
          ...newFilter,
        }));
      }, 500);
    },
    [setAdvancedSearchFilter],
  );

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
          {searchFilterInitialized && (
            <>
              <MemberAdvancedSearch
                otherContactMode={otherContactMode}
                resetCallback={handleResetAll}
                updateState={updateState}
                exportCallback={handleExport}
              />
              <Divider />
            </>
          )}
        </form>
      </FormProvider>
      <Box ref={scrollRef} />
      {userActivatedSearch && searchFilter && searchFilterInitialized && (
        <MemberDataGrid
          otherContactMode={otherContactMode}
          searchFilter={searchFilter}
          searchFilterCallback={handleSearchFilterCallback}
          checkboxSelection
        />
      )}
    </>
  );
};
